import axios from 'axios';

// Configuración base de axios
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

// Interceptor para agregar token de autenticación (futuro)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Error de servidor con respuesta
      console.error('Error de API:', error.response.data);

      // Token vencido/inválido: avisa a AuthContext para que cierre la sesión
      // automáticamente y redirija a login. Se excluye la propia petición de
      // login (ahí un 401 es "credenciales incorrectas", no una sesión vencida).
      if (error.response.status === 401 && !error.config?.url?.includes('/auth/login')) {
        window.dispatchEvent(new Event('auth:session-expired'));
      }

      return Promise.reject(error.response.data);
    } else if (error.request) {
      // Error de red
      console.error('Error de red:', error.request);
      return Promise.reject({ message: 'Error de conexión con el servidor' });
    } else {
      // Otro tipo de error
      console.error('Error:', error.message);
      return Promise.reject({ message: error.message });
    }
  }
);

// =====================
// CLIENTES API
// =====================

export const clientesAPI = {
  getAll: () => api.get('/clientes'),
  getById: (id) => api.get(`/clientes/${id}`),
  create: (clienteData) => api.post('/clientes', clienteData),
  update: (id, clienteData) => api.put(`/clientes/${id}`, clienteData),
  delete: (id) => api.delete(`/clientes/${id}`),
  getSubclientes: (id) => api.get(`/clientes/${id}/sub-clientes`),
  createSubcliente: (id, data) => api.post(`/clientes/${id}/sub-clientes`, data),
  separarSubcliente: (id) => api.post(`/clientes/${id}/separar`),
  importarPreview: (filas) => api.post('/clientes/importar/preview', { filas }),
  importarConfirmar: (grupos) => api.post('/clientes/importar/confirmar', { grupos }),
  importarExportar: () => api.get('/clientes/importar/exportar'),
  eliminarTodos: (codigo) => api.post('/clientes/eliminar-todos', { codigo }),
  getPapelera: () => api.get('/clientes/papelera'),
  restaurarCliente: (id) => api.post(`/clientes/papelera/${id}/restaurar`),
  vaciarPapelera: (codigo) => api.post('/clientes/papelera/vaciar', { codigo }),
};

// =====================
// RECIBOS API
// =====================

export const recibosAPI = {
  getAll: (params = {}) => api.get('/recibos', { params }),
  getById: (id) => api.get(`/recibos/${id}`),
  getByCliente: (clienteId, params = {}) =>
    api.get(`/recibos/cliente/${clienteId}`, { params }),
  getByClienteDesglose: (clienteId) =>
    api.get(`/recibos/cliente/${clienteId}/desglose`),
  create: (reciboData) => api.post('/recibos', reciboData),
  update: (id, reciboData) => api.put(`/recibos/${id}`, reciboData),
  delete: (id) => api.delete(`/recibos/${id}`),
  cancelar: (id, data) => api.put(`/recibos/${id}/cancelar`, data),
};

// =====================
// RECIBOS RECURRENTES API
// =====================

export const recibosRecurrentesAPI = {
  getAll: (params = {}) => api.get('/recibos-recurrentes', { params }),
  getById: (id) => api.get(`/recibos-recurrentes/${id}`),
  getByCliente: (clienteId) => api.get(`/recibos-recurrentes/cliente/${clienteId}`),
  create: (reciboData) => api.post('/recibos-recurrentes', reciboData),
  update: (id, reciboData) => api.put(`/recibos-recurrentes/${id}`, reciboData),
  delete: (id) => api.delete(`/recibos-recurrentes/${id}`),
};

// =====================
// GRUPOS DE CLIENTES API
// =====================

export const gruposAPI = {
  getAll: () => api.get('/grupos'),
  getById: (id) => api.get(`/grupos/${id}`),
  create: (data) => api.post('/grupos', data),
  update: (id, data) => api.put(`/grupos/${id}`, data),
  delete: (id, accion) => api.delete(`/grupos/${id}`, { data: { accion } }),
};

// =====================
// PAGOS API
// =====================

export const pagosAPI = {
  getAll: (params = {}) => api.get('/pagos', { params }),
  getById: (id) => api.get(`/pagos/${id}`),
  getByRecibo: (reciboId) => api.get(`/pagos/recibo/${reciboId}`),
  create: (pagoData) => api.post('/pagos', pagoData),
  update: (id, pagoData) => api.put(`/pagos/${id}`, pagoData),
  delete: (id) => api.delete(`/pagos/${id}`),
};

// =====================
// ALERTAS API
// =====================

export const alertasAPI = {
  getAll: (params = {}) => api.get('/alertas', { params }),
  getById: (id) => api.get(`/alertas/${id}`),
  getEstadisticas: () => api.get('/alertas/estadisticas'),
  create: (alertaData) => api.post('/alertas', alertaData),
  update: (id, alertaData) => api.put(`/alertas/${id}`, alertaData),
  marcarLeidas: (ids) => api.put('/alertas/marcar-leidas', { ids }),
  delete: (id) => api.delete(`/alertas/${id}`),
  generarAutomaticas: () => api.post('/alertas/generar'),
};

// =====================
// REPORTES API
// =====================

export const reportesAPI = {
  getDashboardCompleto: () => api.get('/reportes/dashboard-completo'),
  getIngresosPorMes: (anio) => 
    api.get('/reportes/ingresos-por-mes', { params: { anio } }),
  getClientesTop: (params = {}) => 
    api.get('/reportes/clientes-top', { params }),
  getMetodosPago: (params = {}) => 
    api.get('/reportes/metodos-pago', { params }),
  getEstadoCuentas: (clienteId) => 
    api.get('/reportes/estado-cuentas', { params: { clienteId } }),
  getRecibosVencidos: (diasVencido) => 
    api.get('/reportes/recibos-vencidos', { params: { diasVencido } }),
  getResumenGeneral: (params = {}) => 
    api.get('/reportes/resumen-general', { params }),
};

// =====================
// DASHBOARD API
// =====================

export const dashboardAPI = {
  getStats: () => api.get('/dashboard'),
};

// =====================
// UTILIDADES
// =====================

// Función helper para formatear fechas para la API
export const formatDateForAPI = (date) => {
  if (!date) return null;
  if (typeof date === 'string') return date;
  
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Función helper para parsear respuestas de la API
export const parseAPIResponse = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message,
    };
  }
  return {
    success: false,
    data: null,
    message: 'Respuesta inválida del servidor',
  };
};

// =====================
// WHATSAPP API
// =====================

export const whatsappAPI = {
  enviar: (reciboId) => api.post(`/whatsapp/enviar/${reciboId}`),
  enviarCombinado: (reciboIds) => api.post('/whatsapp/enviar-combinado', { recibo_ids: reciboIds }),
  enviarMultiples: (reciboIds) => api.post('/whatsapp/enviar-multiples', { recibo_ids: reciboIds }),
  getConfigCliente: (clienteId, tipo) => api.get(`/whatsapp/config/cliente/${clienteId}`, { params: { tipo } }),
  updateConfigCliente: (clienteId, data) => api.put(`/whatsapp/config/cliente/${clienteId}`, data),
  enviarAutomaticos: () => api.post('/whatsapp/enviar-automaticos'),
  getLogs: (params = {}) => api.get('/whatsapp/logs', { params }),
};

// =====================
// CONFIGURACIÓN API
// =====================

export const configuracionAPI = {
  // Obtener configuración
  getConfiguracion: () => api.get('/configuracion'),
  updateConfiguracion: (data) => api.put('/configuracion', data),
  getPlanInfo: () => api.get('/configuracion/plan'),
};

// =====================
// RECORDATORIOS API
// =====================

export const recordatoriosAPI = {
  getAll: (params = {}) => api.get('/recordatorios', { params }),
  create: (data) => api.post('/recordatorios', data),
  toggleCompletado: (id) => api.put(`/recordatorios/${id}/completar`),
  delete: (id) => api.delete(`/recordatorios/${id}`),
};

// =====================
// ADJUNTOS API
// =====================

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

export const adjuntosAPI = {
  getByEntidad: (tipo, id) => api.get(`/adjuntos/${tipo}/${id}`),
  upload: (tipo, id, formData) => api.post(`/adjuntos/${tipo}/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/adjuntos/${id}`),
  getUrl: (nombreArchivo) => `${BASE_URL}/uploads/${nombreArchivo}`,
};

// =====================
// AUTENTICACIÓN API
// =====================

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials).then(res => res.data),
  logout: () => api.post('/auth/logout').then(res => res.data),
  verifySession: () => api.get('/auth/verify').then(res => res.data),
  registro: (data) => api.post('/auth/registro', data).then(res => res.data),
};

// =====================
// LEADS API
// =====================

export const leadsAPI = {
  getAll: (params = {}) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  convertir: (id) => api.post(`/leads/${id}/convertir`),
  getHistorial: (id) => api.get(`/leads/${id}/historial`),
  getActividades: (id) => api.get(`/leads/${id}/actividades`),
  createActividad: (id, data) => api.post(`/leads/${id}/actividades`, data),
  deleteActividad: (actId) => api.delete(`/leads/actividades/${actId}`),
};

// =====================
// PROYECTOS TOOLS API
// =====================

export const proyectosAPI = {
  // Tareas
  getTareas: (id) => api.get(`/pedidos/${id}/tareas`),
  createTarea: (id, data) => api.post(`/pedidos/${id}/tareas`, data),
  updateTarea: (id, tid, data) => api.put(`/pedidos/${id}/tareas/${tid}`, data),
  deleteTarea: (id, tid) => api.delete(`/pedidos/${id}/tareas/${tid}`),
  // Gastos
  getGastos: (id) => api.get(`/pedidos/${id}/gastos`),
  createGasto: (id, data) => api.post(`/pedidos/${id}/gastos`, data),
  deleteGasto: (id, gid) => api.delete(`/pedidos/${id}/gastos/${gid}`),
  // Notas
  getNotas: (id) => api.get(`/pedidos/${id}/notas`),
  createNota: (id, data) => api.post(`/pedidos/${id}/notas`, data),
  deleteNota: (id, nid) => api.delete(`/pedidos/${id}/notas/${nid}`),
};

// =====================
// COTIZACIONES API
// =====================

export const cotizacionesAPI = {
  getAll: (params = {}) => api.get('/cotizaciones', { params }),
  getById: (id) => api.get(`/cotizaciones/${id}`),
  create: (data) => api.post('/cotizaciones', data),
  update: (id, data) => api.put(`/cotizaciones/${id}`, data),
  delete: (id) => api.delete(`/cotizaciones/${id}`),
  convertir: (id) => api.post(`/cotizaciones/${id}/convertir`),
};

// =====================
// PIPELINE API
// =====================

export const pipelineAPI = {
  getEtapas: () => api.get('/pipeline/etapas'),
  getOportunidades: () => api.get('/pipeline/oportunidades'),
  createOportunidad: (data) => api.post('/pipeline/oportunidades', data),
  updateOportunidad: (id, data) => api.put(`/pipeline/oportunidades/${id}`, data),
  deleteOportunidad: (id) => api.delete(`/pipeline/oportunidades/${id}`),
};

// =====================
// ACTIVIDADES API
// =====================

export const actividadesAPI = {
  getByCliente: (clienteId) => api.get(`/actividades/cliente/${clienteId}`),
  create: (data) => api.post('/actividades', data),
  delete: (id) => api.delete(`/actividades/${id}`),
};

// =====================
// USUARIOS API
// =====================

export const usuariosAPI = {
  getAll: () => api.get('/usuarios'),
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  deactivate: (id) => api.delete(`/usuarios/${id}`),
  reactivate: (id) => api.patch(`/usuarios/${id}/reactivar`),
};

// =====================
// INVENTARIO API
// =====================

export const inventarioAPI = {
  getAll: (params = {}) => api.get('/inventario', { params }),
  getById: (id) => api.get(`/inventario/${id}`),
  create: (formData) => api.post('/inventario', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/inventario/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/inventario/${id}`),
  updateStock: (id, stock) => api.patch(`/inventario/${id}/stock`, { stock }),
  getImageUrl: (filename) => filename
    ? `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}/uploads/${filename}`
    : null,
};

// =====================
// PEDIDOS / PROYECTOS API
// =====================

// =====================
// DIFUSIÓN API
// =====================

// =====================
// PLANTILLAS WHATSAPP API
// =====================

export const plantillasAPI = {
  getAll: () => api.get('/plantillas'),
  getWabaId: () => api.get('/plantillas/waba-id'),
  create: (data) => api.post('/plantillas', data),
  delete: (wabaId, nombre) => api.delete(`/plantillas/${wabaId}/${nombre}`),
};

export const difusionAPI = {
  getAll: () => api.get('/difusion'),
  getById: (id) => api.get(`/difusion/${id}`),
  enviar: (data) => api.post('/difusion/enviar', data),
  delete: (id) => api.delete(`/difusion/${id}`),
};

export const pedidosAPI = {
  getAll: (params = {}) => api.get('/pedidos', { params }),
  getById: (id) => api.get(`/pedidos/${id}`),
  create: (data) => api.post('/pedidos', data),
  update: (id, data) => api.put(`/pedidos/${id}`, data),
  delete: (id) => api.delete(`/pedidos/${id}`),
};

export const chatAPI = {
  getSessions: () => api.get('/chat/sessions'),
  getSession: (id) => api.get(`/chat/sessions/${id}`),
  renameSession: (id, titulo) => api.post(`/chat/sessions/${id}/rename`, { titulo }),
  deleteSession: (id) => api.delete(`/chat/sessions/${id}`),
  sendMessage: (data) => api.post('/chat/message', data),
};

// =====================
// AI API
// =====================

export const aiAPI = {
  resumenCliente: (clienteId) => api.post('/ai/resumen-cliente', { clienteId }),
  generarDescripcion: (items) => api.post('/ai/generar-descripcion', { items }),
};

// =====================
// MI DÍA API
// =====================

export const miDiaAPI = {
  get: () => api.get('/mi-dia'),
};

// =====================
// METAS API
// =====================

export const metasAPI = {
  getProgreso: ()             => api.get('/metas/progreso'),
  getAll: ()                  => api.get('/metas'),
  getByUsuario: (uid)         => api.get(`/metas/${uid}`),
  set: (uid, data)            => api.put(`/metas/${uid}`, data),
};

export default api;

